import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Section "Acces clients" pour un projet : liste client principal + co-investisseurs
 * + invitations en attente. Permet a l'equipe interne ET au client principal
 * d'inviter un co-investisseur supplementaire.
 */
export default function CoInvestorsSection({ projectId, project }) {
  const { user } = useAuth();
  const [data, setData] = useState({ accesses: [], pending_invitations: [] });
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  // Iter 98 : modale "lien d'invitation prêt" affichee apres invitation
  const [linkData, setLinkData] = useState(null);

  const isInternal = ['carla', 'julie', 'amelie', 'ceo', 'admin', 'collaborateur'].includes(user?.role);
  const isPrimaryClient = user?.role === 'client' && user?.user_id === project?.client_user_id;
  const canInvite = isInternal || isPrimaryClient;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/client-accesses`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement acces clients');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (projectId) load(); }, [projectId, load]);

  const submitInvite = async () => {
    const email = (inviteEmail || '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Email invalide');
      return;
    }
    setInviting(true);
    try {
      const { data: resp } = await axios.post(`${API}/projects/${projectId}/invite-co-investor`, {
        email,
        name: inviteName || null,
      });
      if (resp.added_existing) {
        toast.success('Compte existant ajoute comme co-investisseur');
        setShowInvite(false);
        setInviteEmail('');
        setInviteName('');
        load();
      } else {
        // Iter 98 : ouvre la modale avec lien copiable au lieu d'une copie silencieuse
        setLinkData({
          email,
          name: inviteName || email.split('@')[0],
          magic_link: resp.magic_link || resp.register_link || '',
          email_sent: !!resp.email_sent,
        });
        setShowInvite(false);
        setInviteEmail('');
        setInviteName('');
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur invitation');
    } finally {
      setInviting(false);
    }
  };

  const revoke = async (userId, name) => {
    if (!isInternal) return;
    if (!window.confirm(`Revoquer l'acces de ${name} ? Cette personne ne pourra plus voir le projet.`)) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/client-accesses/${userId}`);
      toast.success('Acces revoque');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur revocation');
    }
  };

  const cancelInvitation = async (invId) => {
    if (!window.confirm('Annuler cette invitation ?')) return;
    try {
      await axios.delete(`${API}/invitations/${invId}`);
      toast.success('Invitation annulee');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur annulation');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="co-investors-loading">
        <div className="animate-pulse h-4 bg-gray-200 rounded w-1/3" />
      </div>
    );
  }

  const totalAccesses = data.accesses.length;
  const pending = data.pending_invitations || [];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="co-investors-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-bold text-[#003E40] flex items-center gap-2">
            <svg className="w-5 h-5 text-[#008A8D]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
            Acces clients
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {totalAccesses} personne{totalAccesses > 1 ? 's' : ''} ont acces a ce projet via le portail client.
            {pending.length > 0 && <> {pending.length} invitation{pending.length > 1 ? 's' : ''} en attente.</>}
          </p>
        </div>
        {canInvite && (
          <button
            onClick={() => setShowInvite((s) => !s)}
            className="px-4 py-2 rounded-lg bg-[#008A8D] text-white hover:bg-[#003E40] text-sm font-medium flex items-center gap-2"
            data-testid="invite-co-investor-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
            Inviter un co-investisseur
          </button>
        )}
      </div>

      {showInvite && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-4" data-testid="invite-co-investor-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Email du co-investisseur *</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="conjoint@email.fr"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#008A8D]"
                data-testid="co-investor-email-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Nom (optionnel)</label>
              <input
                type="text"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="Marie Dupont"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#008A8D]"
                data-testid="co-investor-name-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            <button onClick={() => setShowInvite(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800" data-testid="cancel-invite">
              Annuler
            </button>
            <button
              onClick={submitInvite}
              disabled={inviting}
              className="px-4 py-1.5 rounded-lg bg-[#008A8D] text-white hover:bg-[#003E40] disabled:opacity-50 text-sm font-medium"
              data-testid="submit-invite-co-investor"
            >
              {inviting ? 'Envoi...' : 'Envoyer l\'invitation'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Un email avec lien magique sera envoye. Le co-investisseur pourra creer son compte et acceder au portail client du projet.
          </p>
        </div>
      )}

      {data.accesses.length === 0 && pending.length === 0 ? (
        <div className="text-center py-6 text-gray-500 text-sm" data-testid="no-accesses">
          Aucun acces client configure pour ce projet.
        </div>
      ) : (
        <div className="space-y-2">
          {data.accesses.map((acc) => (
            <div
              key={acc.user_id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                acc.is_primary
                  ? 'bg-teal-50 border-teal-200'
                  : 'bg-white border-gray-200'
              }`}
              data-testid={`access-row-${acc.user_id}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                  acc.is_primary ? 'bg-[#008A8D]' : 'bg-amber-500'
                }`}>
                  {(acc.name || acc.email || '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-medium text-gray-900 flex items-center gap-2">
                    {acc.name || acc.email}
                    {acc.is_primary && <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 font-semibold">Principal</span>}
                    {!acc.is_primary && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">Co-investisseur</span>}
                  </div>
                  <div className="text-xs text-gray-500">{acc.email}</div>
                </div>
              </div>
              {isInternal && !acc.is_primary && (
                <button
                  onClick={() => revoke(acc.user_id, acc.name || acc.email)}
                  className="text-xs px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg font-medium"
                  data-testid={`revoke-${acc.user_id}`}
                >
                  Revoquer
                </button>
              )}
            </div>
          ))}
          {pending.map((inv) => {
            const magicLink = inv.token ? `${window.location.origin}/register?token=${inv.token}` : '';
            const copyLink = async () => {
              try {
                await navigator.clipboard.writeText(magicLink);
                toast.success("Lien d'invitation copie");
              } catch {
                toast.error('Copie impossible');
              }
            };
            return (
            <div
              key={inv.invite_id}
              className="flex items-center justify-between p-3 rounded-lg border border-dashed border-amber-300 bg-amber-50"
              data-testid={`pending-invite-${inv.invite_id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <div>
                  <div className="font-medium text-gray-900">{inv.client_name || inv.email}</div>
                  <div className="text-xs text-gray-500">{inv.email} • invitation en attente</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {magicLink && (
                  <button
                    onClick={copyLink}
                    className="text-xs px-3 py-1.5 text-[#008A8D] hover:bg-teal-100 rounded-lg font-medium border border-teal-300 bg-white"
                    data-testid={`copy-invite-link-${inv.invite_id}`}
                  >
                    Copier lien
                  </button>
                )}
                {isInternal && (
                  <button
                    onClick={() => cancelInvitation(inv.invite_id)}
                    className="text-xs px-3 py-1.5 text-red-600 hover:bg-red-100 rounded-lg font-medium"
                    data-testid={`cancel-invite-${inv.invite_id}`}
                  >
                    Annuler
                  </button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Iter 98 : modale "Lien d'invitation pret" avec copie facile */}
      {linkData && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="co-investor-link-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
              </span>
              <h3 className="text-lg font-bold text-gray-900">Invitation creee</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {linkData.email_sent
                ? `Un email d'invitation a ete envoye a ${linkData.email}. Vous pouvez egalement copier le lien ci-dessous pour le transmettre directement (WhatsApp, SMS, etc.) si l'email tarde.`
                : `L'envoi automatique a echoue. Copiez le lien ci-dessous et transmettez-le manuellement a ${linkData.email}.`}
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase text-gray-500 font-semibold">Email du co-investisseur</p>
                  <p className="text-sm font-mono text-gray-800 truncate">{linkData.email}</p>
                </div>
                <button
                  onClick={async () => { try { await navigator.clipboard.writeText(linkData.email); toast.success('Email copie'); } catch { toast.error('Copie impossible'); } }}
                  className="text-xs px-2 py-1 rounded bg-white border border-gray-300 hover:bg-gray-50 flex-shrink-0"
                  data-testid="co-investor-copy-email"
                >Copier</button>
              </div>
              <div className="flex items-center justify-between gap-2 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase text-blue-700 font-semibold">Lien d&apos;invitation (valide 7 jours)</p>
                  <p className="text-xs font-mono text-blue-900 truncate" title={linkData.magic_link}>{linkData.magic_link}</p>
                </div>
                <button
                  onClick={async () => { try { await navigator.clipboard.writeText(linkData.magic_link); toast.success('Lien copie'); } catch { toast.error('Copie impossible'); } }}
                  className="text-xs px-2 py-1 rounded bg-white border border-blue-300 hover:bg-blue-100 font-semibold flex-shrink-0"
                  data-testid="co-investor-copy-link"
                >Copier le lien</button>
              </div>
            </div>
            <div className="flex justify-end mt-5">
              <button
                onClick={() => setLinkData(null)}
                className="px-4 py-2 rounded-md bg-[#008A8D] hover:bg-[#003E40] text-white text-sm font-semibold"
                data-testid="co-investor-link-close"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
